import DOMPurify from "dompurify";
import {ReactNode, useState} from "react";
import {useNavigate} from "react-router";

import {DeleteActionDialog} from "@/components/deleteActionDialog/DeleteActionDialog.tsx";
import {ActionDialog} from "@/components/overlay/ActionDialog.tsx";
import {
  DropdownAction,
  DropdownActionItem,
  DropdownItem,
  DropDownLink,
  DropDownReactLink,
} from "@/components/quickActionsBar/DropdownAction.tsx";
import {DropdownDivider} from "@/components/quickActionsBar/DropdownDivider.tsx";
import {QuickActionsBar} from "@/components/quickActionsBar/QuickActionsBar.tsx";
import {IncidentType, StatusPageTheme} from "@/lib/types/statuspages.ts";
import {IncidentCreateAction} from "@/pages/incidents/actions/IncidentCreateAction.tsx";
import {deleteStatusPage, duplicateStatusPage} from "@/pages/statuspages/utils/quickActions.ts";

type StatusPageActionProps = {
  id: number;
  name: string;
  theme: StatusPageTheme;
  isServedBySpa: boolean;
  hasWriteAccess: boolean;
  hasAdminAccess: boolean;
};

export const StatusPageActions = ({
  id,
  name,
  theme,
  isServedBySpa,
  hasWriteAccess,
  hasAdminAccess,
}: StatusPageActionProps) => {
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const navigate = useNavigate();
  const actions: ReactNode[] = [];
  const dropdownItems: DropdownItem[] = [];
  if (hasWriteAccess) {
    dropdownItems.push(<DropDownReactLink text={"Manage Page"} to={`/statuspages/${id}`} />);
    dropdownItems.push(
      <IncidentCreateAction statusPageId={id} variant="dropdownMenuItem">
        Add Incident
      </IncidentCreateAction>
    );
    dropdownItems.push(
      <IncidentCreateAction
        statusPageId={id}
        variant="dropdownMenuItem"
        className="text-nowrap"
        incidentType={IncidentType.SCHEDULED_MAINTENANCE}
      >
        Schedule Maintenance
      </IncidentCreateAction>
    );
    dropdownItems.push(<DropDownReactLink text={"Manage Subscribers"} to={`/statuspages/${id}?tab=subscribers`} />);
    // Once the page is actually served by the SPA there is nothing left to preview.
    if (theme !== StatusPageTheme.LEGACY && !isServedBySpa) {
      dropdownItems.push(
        <DropDownLink
          text="Preview Inspire 2.0"
          href={`/status-page/private/${id}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="dropdownMenuItemHighlightGreen"
        />
      );
    }
    if (dropdownItems.length > 0) {
      dropdownItems.push(<DropdownDivider />);
    }
    dropdownItems.push(
      <DropdownActionItem
        text="Duplicate"
        onClick={async () => {
          duplicateStatusPage(id)
            .then((newId) => {
              navigate(`/statuspages/${newId}`);
            })
            .catch((error) => {
              setDuplicateError(error);
            });
        }}
      />
    );
    if (hasAdminAccess) {
      dropdownItems.push(<DropdownActionItem text="Delete" onClick={() => setShowConfirmDelete(true)} />);
    }
  }
  if (dropdownItems.length > 0) {
    actions.push(<DropdownAction id="status-page-dropdown-action-items" items={dropdownItems} />);
  }

  return (
    <>
      <DeleteActionDialog
        show={showConfirmDelete}
        title="Delete status page"
        onCancel={() => setShowConfirmDelete(false)}
        onConfirm={async () => {
          await deleteStatusPage(id);
          setShowConfirmDelete(false);
        }}
      >
        Are you sure you want to permanently delete the status page <strong>{name}</strong> along with all Subscribers?
      </DeleteActionDialog>
      <ActionDialog
        show={duplicateError !== null}
        title="Duplicate Status Page"
        onCancel={() => setDuplicateError(null)}
        onConfirm={() => setDuplicateError(null)}
      >
        <span dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(duplicateError || "")}}></span>
      </ActionDialog>
      <QuickActionsBar id="status-page-quick-actions" actions={actions} />
    </>
  );
};
